import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  Rating,
  TextField,
  InputAdornment,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import SearchIcon from '@mui/icons-material/Search';
import ReplyIcon from '@mui/icons-material/Reply';
import PersonIcon from '@mui/icons-material/Person';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailIcon from '@mui/icons-material/Email';
import { collection, getDocs, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface Message {
  id: string;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  formType: 'contact' | 'feedback';
  category?: string;
  rating?: number;
  read: boolean;
  createdAt: any;
  adminReply?: string;
  repliedAt?: any;
  userId?: string;
  userEmail?: string;
}

export default function MessagesManager() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0); // 0=all, 1=contact, 2=feedback
  const [search, setSearch] = useState('');
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [registeredEmails, setRegisteredEmails] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      setLoading(true);
      // Load registered users to cross-reference emails
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const emails = new Set<string>();
      usersSnapshot.forEach((d) => {
        const email = d.data().email;
        if (email) emails.add(email.toLowerCase());
      });
      setRegisteredEmails(emails);

      const querySnapshot = await getDocs(collection(db, 'contact_messages'));
      const data: Message[] = [];
      querySnapshot.forEach((d) => {
        data.push({ id: d.id, ...d.data() } as Message);
      });
      data.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return bTime - aTime;
      });
      setMessages(data);
    } catch (error) {
      console.error('Error loading messages:', error);
      setSnackbar({ open: true, message: 'Error loading messages', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Determine user status: 'logged-in' (submitted while logged in), 'registered' (has account but wasn't logged in), 'guest' (no account)
  const getUserStatus = (msg: Message): 'logged-in' | 'registered' | 'guest' => {
    if (msg.userId) return 'logged-in';
    if (msg.email && registeredEmails.has(msg.email.toLowerCase())) return 'registered';
    return 'guest';
  };

  const handleReply = async () => {
    if (!selectedMessage || !replyText.trim()) return;
    setReplying(true);
    try {
      await updateDoc(doc(db, 'contact_messages', selectedMessage.id), {
        adminReply: replyText.trim(),
        repliedAt: serverTimestamp(),
      });
      const updated = { ...selectedMessage, adminReply: replyText.trim(), repliedAt: new Date() };
      setSelectedMessage(updated);
      setMessages((prev) => prev.map((m) => (m.id === selectedMessage.id ? updated : m)));
      setReplyText('');
      setSnackbar({ open: true, message: 'Reply saved successfully', severity: 'success' });
    } catch (err) {
      console.error('Error saving reply:', err);
      setSnackbar({ open: true, message: 'Error saving reply', severity: 'error' });
    } finally {
      setReplying(false);
    }
  };

  const handleView = async (msg: Message) => {
    setSelectedMessage(msg);
    setReplyText(msg.adminReply || '');
    setDialogOpen(true);
    if (!msg.read) {
      try {
        await updateDoc(doc(db, 'contact_messages', msg.id), { read: true });
        setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, read: true } : m)));
      } catch (err) {
        console.error('Error marking as read:', err);
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      await deleteDoc(doc(db, 'contact_messages', id));
      setMessages((prev) => prev.filter((m) => m.id !== id));
      setDialogOpen(false);
      setSnackbar({ open: true, message: 'Message deleted', severity: 'success' });
    } catch (error) {
      console.error('Error deleting message:', error);
      setSnackbar({ open: true, message: 'Error deleting message', severity: 'error' });
    }
  };

  const filtered = messages.filter((m) => {
    if (tab === 1 && m.formType !== 'contact') return false;
    if (tab === 2 && m.formType !== 'feedback') return false;
    if (search) {
      const s = search.toLowerCase();
      return (
        m.name?.toLowerCase().includes(s) ||
        m.email?.toLowerCase().includes(s) ||
        m.subject?.toLowerCase().includes(s) ||
        m.message?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  const unreadCount = messages.filter((m) => !m.read).length;

  const formatDate = (ts: any) => {
    if (!ts?.toDate) return '—';
    return ts.toDate().toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Messages & Feedback
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {messages.length} total messages{unreadCount > 0 && ` · ${unreadCount} unread`}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ '& .MuiTab-root': { fontWeight: 600 } }}>
          <Tab label={`All (${messages.length})`} />
          <Tab label={`Contact (${messages.filter((m) => m.formType === 'contact').length})`} />
          <Tab label={`Feedback (${messages.filter((m) => m.formType === 'feedback').length})`} />
        </Tabs>
        <TextField
          size="small"
          placeholder="Search messages..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ ml: 'auto', minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  No messages found
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((msg) => (
                <TableRow
                  key={msg.id}
                  sx={{
                    bgcolor: msg.read ? 'transparent' : 'rgba(212,175,55,0.05)',
                    fontWeight: msg.read ? 400 : 700,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                  }}
                  onClick={() => handleView(msg)}
                >
                  <TableCell sx={{ fontWeight: msg.read ? 400 : 700 }}>{msg.name}</TableCell>
                  <TableCell sx={{ fontWeight: msg.read ? 400 : 700, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {msg.subject || '(no subject)'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={msg.formType === 'feedback' ? 'Feedback' : 'Contact'}
                      size="small"
                      sx={{
                        bgcolor: msg.formType === 'feedback' ? '#ec489915' : '#3b82f615',
                        color: msg.formType === 'feedback' ? '#be185d' : '#2563eb',
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const status = getUserStatus(msg);
                      return (
                        <Chip
                          icon={status === 'logged-in' ? <PersonIcon sx={{ fontSize: '16px !important' }} /> : <PersonOutlineIcon sx={{ fontSize: '16px !important' }} />}
                          label={status === 'logged-in' ? 'Logged In' : status === 'registered' ? 'Registered' : 'Guest'}
                          size="small"
                          sx={{
                            bgcolor: status === 'logged-in' ? '#e8f5e9' : status === 'registered' ? '#e3f2fd' : '#fff3e0',
                            color: status === 'logged-in' ? '#2e7d32' : status === 'registered' ? '#1565c0' : '#e65100',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{formatDate(msg.createdAt)}</TableCell>
                  <TableCell>
                    {msg.adminReply ? (
                      <Chip label="Replied" size="small" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }} />
                    ) : msg.read ? (
                      <Chip label="Read" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
                    ) : (
                      <Chip label="New" size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleView(msg); }} title="View">
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(msg.id); }} title="Delete" sx={{ color: '#d32f2f' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Message Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        {selectedMessage && (
          <>
            <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <MarkEmailReadIcon sx={{ color: GOLD }} />
              {selectedMessage.formType === 'feedback' ? 'Feedback' : 'Contact Message'}
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: 'grid', gap: 2 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Name</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{selectedMessage.name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{selectedMessage.email}</Typography>
                  </Box>
                </Box>
                {selectedMessage.phone && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Phone</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{selectedMessage.phone}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">User Status</Typography>
                  {(() => {
                    const status = getUserStatus(selectedMessage);
                    return (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Chip
                            icon={status === 'logged-in' ? <PersonIcon sx={{ fontSize: '16px !important' }} /> : <PersonOutlineIcon sx={{ fontSize: '16px !important' }} />}
                            label={
                              status === 'logged-in' ? 'Logged In User' :
                              status === 'registered' ? 'Registered User (Not logged in when sent)' :
                              'Guest (No Account)'
                            }
                            size="small"
                            sx={{
                              bgcolor: status === 'logged-in' ? '#e8f5e9' : status === 'registered' ? '#e3f2fd' : '#fff3e0',
                              color: status === 'logged-in' ? '#2e7d32' : status === 'registered' ? '#1565c0' : '#e65100',
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                        {status === 'logged-in' && selectedMessage.userEmail && (
                          <Typography sx={{ fontSize: '0.8rem', color: '#666', mt: 0.5 }}>
                            Account: {selectedMessage.userEmail}
                          </Typography>
                        )}
                        {status === 'registered' && (
                          <Typography sx={{ fontSize: '0.8rem', color: '#1565c0', mt: 0.5 }}>
                            This user has an account ({selectedMessage.email}) but was not logged in when sending this message.
                          </Typography>
                        )}
                      </>
                    );
                  })()}
                </Box>
                {selectedMessage.category && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Category</Typography>
                    <Chip label={selectedMessage.category} size="small" sx={{ mt: 0.5, fontWeight: 600 }} />
                  </Box>
                )}
                {selectedMessage.rating != null && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Rating</Typography>
                    <Rating value={selectedMessage.rating} readOnly size="small" sx={{ mt: 0.5 }} />
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">Subject</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{selectedMessage.subject || '(no subject)'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Message</Typography>
                  <Paper sx={{ p: 2, mt: 0.5, bgcolor: '#f9f9f9', borderRadius: 2 }}>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{selectedMessage.message}</Typography>
                  </Paper>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Received</Typography>
                  <Typography sx={{ fontSize: '0.85rem' }}>{formatDate(selectedMessage.createdAt)}</Typography>
                </Box>

                {/* Existing reply */}
                {selectedMessage.adminReply && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Admin Reply</Typography>
                    <Paper sx={{ p: 2, mt: 0.5, bgcolor: '#e8f5e9', borderRadius: 2, border: '1px solid #c8e6c9' }}>
                      <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>{selectedMessage.adminReply}</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: '#999', mt: 1 }}>Replied: {formatDate(selectedMessage.repliedAt)}</Typography>
                    </Paper>
                  </Box>
                )}

                {/* Reply input */}
                {(() => {
                  const status = getUserStatus(selectedMessage);
                  if (status === 'guest') {
                    return (
                      <Paper sx={{ p: 2, bgcolor: '#fff8e1', borderRadius: 2, border: '1px solid #ffe082' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <EmailIcon sx={{ color: '#f57f17', fontSize: 20 }} />
                          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#e65100' }}>
                            Manual Reply Required
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.85rem', color: '#555', mb: 1 }}>
                          This message was sent by a guest (no account). Please reply manually via email.
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#fff', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                          <EmailIcon sx={{ color: '#1565c0', fontSize: 18 }} />
                          <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1565c0' }}>
                            {selectedMessage.email}
                          </Typography>
                        </Box>
                      </Paper>
                    );
                  }
                  // logged-in or registered user — allow system reply
                  return (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {selectedMessage.adminReply ? 'Update Reply' : 'Write Reply'}
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Type your reply here..."
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  );
                })()}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => handleDelete(selectedMessage.id)} color="error" startIcon={<DeleteIcon />}>
                Delete
              </Button>
              {getUserStatus(selectedMessage) !== 'guest' && (
                <Button
                  onClick={handleReply}
                  disabled={!replyText.trim() || replying}
                  variant="contained"
                  startIcon={<ReplyIcon />}
                  sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                >
                  {replying ? 'Saving...' : (selectedMessage.adminReply ? 'Update Reply' : 'Send Reply')}
                </Button>
              )}
              <Button onClick={() => setDialogOpen(false)} variant="outlined">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
